import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ShieldCheck, Smartphone, Camera, Video, ArrowRight, Building2, Hotel, Car, Briefcase, Cpu, Wrench, Sofa, Trophy, Shirt, Watch, ShoppingBag, Cog, GraduationCap, Handshake } from "lucide-react";
import { fetchAnnonces, fetchStats, fetchStatsByCategory } from "@/lib/api";
import { CATEGORIES, withWatermark } from "@/lib/utils";
import { useCountry } from "@/lib/CountryContext";
import AnnonceCard from "@/components/AnnonceCard";
import ModeUniverseSection from "@/components/ModeUniverseSection";
import CountryAnnonceCounter from "@/components/CountryAnnonceCounter";
import { OffreLancementTeaser } from "@/components/OffreLancementTeaser";
import useFreshAnnonceToast from "@/lib/useFreshAnnonceToast";
import useLiveAnnonceStream from "@/lib/useLiveAnnonceStream";

const catIcons = {
  Immobilier: Building2, Hotelleries: Hotel, Vehicules: Car, "Equipement Auto": Cog, Emploi: Briefcase, Formation: GraduationCap, Entreprise: Handshake,
  Electronique: Cpu, "Materiel pro": Wrench, Mobiliers: Sofa, Loisir: Trophy,
  Mode: Shirt, "Montres & Bijoux": Watch, "Accessoires & Bagagerie": ShoppingBag,
};

const QUARTIERS = ["Cocody", "Riviera", "2 Plateaux", "Angre", "Palmeraie", "Marcory", "Bietry", "Zone 4", "Plateau", "Treichville", "Yopougon", "Abobo", "Adjame", "Attiecoube", "Koumassi", "Port-Bouet", "Vridi", "Bingerville", "Anyama", "Songon"];

export default function HomePage() {
  const navigate = useNavigate();
  const { code: countryCode, country } = useCountry();
  useFreshAnnonceToast();
  useLiveAnnonceStream();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [city, setCity] = useState("");
  const [featured, setFeatured] = useState([]);
  const [stats, setStats] = useState(null);
  const [catCounts, setCatCounts] = useState({});

  useEffect(() => {
    fetchAnnonces({ page_size: 4, country: countryCode }).then((d) => setFeatured((d.items || d).slice(0, 4))).catch(() => {});
    fetchStats().then(setStats).catch(() => {});
    fetchStatsByCategory(countryCode).then((d) => setCatCounts(d.counts || {})).catch(() => {});
  }, [countryCode]);

  const submitSearch = (e) => {
    e?.preventDefault();
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (cat) params.set("category", cat);
    if (city) params.set("city", city);
    navigate(`/annonces?${params.toString()}`);
  };

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 pt-14 pb-10 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
        <div className="rise">
          <div
            className="inline-block text-[12px] tracking-widest font-bold uppercase mb-5"
            style={{ color: "var(--brand)" }}
            data-testid="home-tagline"
          >
            Petites annonces nouvelle generation {country.flag} {country.name}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight">
            Achetez, vendez, louez…<br />
            <span className="brand-gradient-text">sans limites en toute sécurité</span><br />
            <span className="text-[var(--ink)]">avec Fodindin</span>
          </h1>
          <p className="mt-6 text-[var(--ink-soft)] max-w-xl">
            Fodindin combine petites annonces gratuites, experiences premium, localisations
            detaillees a {country.capital}, module hotelier intelligent et monetisation adaptee au marche local.
          </p>

          <form onSubmit={submitSearch} className="mt-7 glass-card p-2 flex flex-wrap items-center gap-2" data-testid="home-search-form">
            <div className="flex items-center gap-2 px-3 flex-1 min-w-[180px]">
              <Search size={18} className="text-[var(--ink-soft)]" />
              <input
                type="text"
                placeholder="Rechercher une villa, FD-000123, ..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="!border-0 !shadow-none !p-2 bg-transparent"
                style={{ outline: "none", boxShadow: "none", border: "none" }}
                data-testid="home-search-input"
              />
            </div>
            <select value={cat} onChange={(e) => setCat(e.target.value)} className="!w-auto min-w-[130px]" data-testid="home-search-cat">
              <option value="">Toutes</option>
              {CATEGORIES.map((c) => <option key={c.key}>{c.key}</option>)}
            </select>
            <select value={city} onChange={(e) => setCity(e.target.value)} className="!w-auto min-w-[130px]" data-testid="home-search-city">
              <option value="">{country.capital}</option>
              {country.cities.map((c) => <option key={c}>{c}</option>)}
            </select>
            <button type="submit" className="btn-green" data-testid="home-search-submit">Rechercher</button>
          </form>

          <div className="mt-6 flex flex-wrap gap-2">
            <span className="chip"><ShieldCheck size={14} /> HTTPS securise</span>
            <span className="chip"><Smartphone size={14} /> Wave / Orange Money / MTN</span>
            <span className="chip"><Camera size={14} /> 2 photos gratuites</span>
            <span className="chip"><Camera size={14} /> 4 photos premium</span>
            <span className="chip"><Video size={14} /> Video immobiliere 10 s</span>
          </div>
        </div>

        <div className="relative rise delay-2">
          <img
            src={withWatermark("https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=1100&q=80")}
            alt="Immobilier premium"
            className="w-full h-[480px] object-cover object-bottom rounded-[28px] shadow-xl"
          />
          <div className="absolute -top-4 right-6 glass-card px-5 py-4 w-56">
            <div className="text-orange-600 font-extrabold text-xl">+40%</div>
            <div className="text-xs text-[var(--ink-soft)]">de remise hoteliere en heures creuses</div>
          </div>
          <div className="absolute bottom-6 left-6 right-24 glass-card px-5 py-5">
            <div className="text-[11px] uppercase tracking-widest text-[var(--ink-soft)] mb-1">Immobilier premium</div>
            <div className="font-bold text-[var(--ink)]">Des annonces qui convertissent mieux</div>
            <div className="text-xs text-[var(--ink-soft)] mt-1">
              Photos plus valorisees, details visibles, options premium et meilleure presentation des vendeurs.
            </div>
          </div>
          <div className="absolute -bottom-4 left-10 glass-card px-5 py-3">
            <div className="text-orange-600 font-extrabold text-lg">2000 FCFA</div>
            <div className="text-[10px] text-[var(--ink-soft)]">pour maintenir la visibilite detaillee 1 mois</div>
          </div>
        </div>
      </section>

      {/* MARCHÉ UEMOA — compteur d'annonces par pays */}
      <CountryAnnonceCounter />

      <OffreLancementTeaser />

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-6 mt-16">
        <div className="flex items-end justify-between mb-7">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight">Les univers Fodindin</h2>
            <p className="text-[var(--ink-soft)] mt-1">Une organisation claire et haut de gamme pour particuliers, pros, agences et hoteliers.</p>
          </div>
          <Link to="/annonces" className="hidden md:inline-flex items-center gap-1 text-orange-600 font-semibold" data-testid="link-voir-annonces">
            Voir toutes les annonces <ArrowRight size={16} />
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {CATEGORIES.map((c, i) => {
            const Icon = catIcons[c.key];
            return (
              <Link
                key={c.key}
                to={`/annonces?category=${encodeURIComponent(c.key)}`}
                className="card p-5 flex flex-col gap-3 rise"
                style={{ animationDelay: `${i * 0.04}s` }}
                data-testid={`cat-card-${c.key}`}
              >
                <div className="w-12 h-12 rounded-2xl bg-orange-50 text-orange-600 grid place-items-center font-bold">
                  {Icon ? <Icon size={22} /> : c.code}
                </div>
                <div className="flex items-center justify-between gap-2">
                  <div className="font-bold text-[15px]">{c.key}</div>
                  {catCounts[c.key] > 0 && (
                    <span className="text-[11px] font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full whitespace-nowrap" data-testid={`cat-count-${c.key}`}>
                      {catCounts[c.key].toLocaleString("fr-FR")} annonce{catCounts[c.key] > 1 ? "s" : ""}
                    </span>
                  )}
                </div>
                <div className="text-xs text-[var(--ink-soft)] leading-relaxed">{c.desc}</div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* MODE / MONTRES / ACCESSOIRES Universe */}
      <ModeUniverseSection />

      {/* LOCALISATION */}
      <section className="max-w-7xl mx-auto px-6 mt-16">
        <div className="glass-card p-8 md:p-10">
          <h2 className="font-display text-3xl font-extrabold tracking-tight">Localisation detaillee en {country.name}</h2>
          <p className="text-[var(--ink-soft)] mt-1">
            {countryCode === "CI"
              ? "Abidjan dispose d'une granularite plus fine pour une meilleure precision de recherche."
              : `Recherche par ville et quartier dans toutes les regions du ${country.name}.`}
          </p>

          <div className="mt-7 grid grid-cols-1 md:grid-cols-3 gap-6">
            {countryCode === "CI" && (
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-[var(--ink-soft)] mb-3">Abidjan - Quartiers</div>
                <div className="flex flex-wrap gap-2">
                  {QUARTIERS.map((q) => (
                    <span key={q} className="chip" style={{ fontSize: 12 }}>{q}</span>
                  ))}
                </div>
              </div>
            )}
            <div className={countryCode === "CI" ? "" : "md:col-span-2"}>
              <div className="text-xs font-bold uppercase tracking-widest text-[var(--ink-soft)] mb-3">Grandes villes</div>
              <div className="flex flex-wrap gap-2">
                {country.cities.map((q) => (
                  <span key={q} className="chip" style={{ fontSize: 12 }}>{q}</span>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-[var(--ink-soft)] mb-3">Points forts</div>
              <ul className="space-y-2 text-sm">
                {["Recherche par ville, commune et quartier", "Filtres lisibles et rapides", "Navigation mobile optimisee", "Experience premium localisee"].map((p) => (
                  <li key={p} className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-orange-500" />{p}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* HOTEL CTA */}
      <section className="max-w-7xl mx-auto px-6 mt-16">
        <div className="rounded-[28px] overflow-hidden grid grid-cols-1 lg:grid-cols-2 bg-gradient-to-br from-orange-500 to-orange-400 text-white">
          <div className="p-10">
            <div className="text-[12px] uppercase tracking-widest opacity-90">Hotelleries intelligentes</div>
            <h3 className="font-display text-3xl md:text-4xl font-extrabold mt-3">Maximiser le taux d&apos;occupation des hotels de {country.capital}</h3>
            <p className="opacity-95 mt-3 max-w-md">
              Fodindin aide les etablissements partenaires a vendre leurs chambres libres avec
              des remises intelligentes selon les heures creuses ou les horaires de forte affluence.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              <Link to="/hotelleries" className="btn-secondary" data-testid="home-hotel-rooms">Voir les chambres</Link>
              <Link to="/hotelleries/h1" className="btn-secondary" data-testid="home-hotel-fiche">Voir une fiche hotel</Link>
            </div>
          </div>
          <div className="relative min-h-[260px]">
            <img src={withWatermark("https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?auto=format&fit=crop&w=1100&q=80")} alt="Hotel" className="absolute inset-0 w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* YANGO LIVRAISON CTA */}
      <section className="max-w-7xl mx-auto px-6 mt-16">
        <div className="rounded-[28px] overflow-hidden grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] bg-white border border-orange-200">
          <div className="p-10 bg-gradient-to-br from-orange-500 to-red-500 text-white">
            <div className="text-[12px] uppercase tracking-widest opacity-90">Livraison express partenaire</div>
            <h3 className="font-display text-3xl md:text-4xl font-extrabold mt-3">Yango livre vos achats en 24-48 h</h3>
            <p className="opacity-95 mt-3 max-w-md">
              Abidjan et villes a moins de 100 km. Frais transparents calcules en temps reel, suivi inclus et appel direct au transporteur.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              <Link to="/annonces?category=Electronique" className="btn-secondary !text-orange-700" data-testid="home-yango-elec">Electronique</Link>
              <Link to="/annonces?category=Mobiliers" className="btn-secondary !text-orange-700" data-testid="home-yango-mob">Mobiliers</Link>
              <Link to="/annonces?category=Materiel pro" className="btn-secondary !text-orange-700" data-testid="home-yango-mp">Materiel pro</Link>
            </div>
          </div>
          <div className="p-8 flex flex-col justify-center gap-4">
            <div className="flex gap-3 items-start">
              <div className="w-10 h-10 rounded-2xl bg-orange-50 text-orange-600 grid place-items-center font-bold">🚚</div>
              <div><div className="font-bold">Frais calcules en direct</div><div className="text-sm text-[var(--ink-soft)]">A partir de 2 500 FCFA, plafonnes a 15 000 FCFA.</div></div>
            </div>
            <div className="flex gap-3 items-start">
              <div className="w-10 h-10 rounded-2xl bg-orange-50 text-orange-600 grid place-items-center font-bold">📍</div>
              <div><div className="font-bold">Couverture etendue</div><div className="text-sm text-[var(--ink-soft)]">Abidjan + villes a moins de 100 km.</div></div>
            </div>
            <div className="flex gap-3 items-start">
              <div className="w-10 h-10 rounded-2xl bg-orange-50 text-orange-600 grid place-items-center font-bold">📞</div>
              <div><div className="font-bold">Appel direct au transporteur</div><div className="text-sm text-[var(--ink-soft)]">Disponible depuis chaque fiche annonce eligible.</div></div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-6 mt-16">
        <div className="flex items-end justify-between mb-7">
          <div>
            <h2 className="font-display text-3xl font-extrabold tracking-tight">Annonces en vedette</h2>
            <p className="text-[var(--ink-soft)] mt-1">Un rendu plus premium, avec favoris persistants et cartes mieux structurees.</p>
          </div>
          <Link to="/annonces" className="hidden md:inline-flex items-center gap-1 text-orange-600 font-semibold" data-testid="featured-see-all">
            Tout voir <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          {featured.map((a, i) => (
            <div key={a.id} className="rise" style={{ animationDelay: `${i * 0.05}s` }}>
              <AnnonceCard a={a} />
            </div>
          ))}
        </div>
      </section>

      {/* STATS */}
      {stats && (
        <section className="max-w-7xl mx-auto px-6 mt-16" data-testid="stats-section">
          <div className="glass-card p-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div><div className="text-3xl font-extrabold text-orange-600">{stats.visitors_today.toLocaleString("fr-FR")}</div><div className="text-xs text-[var(--ink-soft)] mt-1">Visiteurs aujourd'hui</div></div>
            <div><div className="text-3xl font-extrabold text-orange-600">{stats.active_annonces.toLocaleString("fr-FR")}</div><div className="text-xs text-[var(--ink-soft)] mt-1">Annonces actives</div></div>
            <div><div className="text-3xl font-extrabold text-orange-600">{stats.new_per_day.toLocaleString("fr-FR")}</div><div className="text-xs text-[var(--ink-soft)] mt-1">Nouvelles annonces / jour</div></div>
            <div><div className="text-3xl font-extrabold text-orange-600">{stats.partner_hotels.toLocaleString("fr-FR")}</div><div className="text-xs text-[var(--ink-soft)] mt-1">Hotels partenaires</div></div>
          </div>
        </section>
      )}
    </main>
  );
}
